import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Container, Typography, Select, MenuItem, Grid, Paper, Button, Box,
  CircularProgress, Stack, ToggleButtonGroup, ToggleButton, TextField, Divider
} from '@mui/material';
import { get, post } from './api';
import { useAuth } from './authContext';
import { useNavigate } from 'react-router-dom';
import { formattedDate } from './sharedUtils';

const Dashboard = () => {
  // --- helpers ---
  const formatMonthKey = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    return `${y}${m}`; // YYYYMM
  };

  const formatYMD = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  // "20240918,21:04:36+0000" -> Date
  const parseCustomerDate = (dateString) => {
    const [datePart, timePart] = dateString.split(',');
    const year = parseInt(datePart.slice(0, 4), 10);
    const month = parseInt(datePart.slice(4, 6), 10) - 1;
    const day = parseInt(datePart.slice(6, 8), 10);
    const [time] = timePart.split('+');
    const [hours, minutes, seconds] = time.split(':').map(Number);
    return new Date(Date.UTC(year, month, day, hours, minutes, seconds));
  };

  const monthsBetween = (start, end) => {
    const keys = [];
    const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
    const last = new Date(end.getFullYear(), end.getMonth(), 1);
    while (cursor <= last) {
      keys.push(formatMonthKey(cursor));
      cursor.setMonth(cursor.getMonth() + 1);
    }
    return keys;
  };

  const dateFromYMD = (s) => {
    const [y, m, d] = s.split('-').map(Number);
    return new Date(y, m - 1, d);
  };

  // --- hooks / state ---
  const { customer, logout } = useAuth();
  const navigate = useNavigate();

  const [mode, setMode] = useState('month'); // 'month' | 'range'
  const [months, setMonths] = useState([]);
  const [month, setMonth] = useState(formatMonthKey(new Date()));

  const today = useMemo(() => new Date(), []);
  const [startDate, setStartDate] = useState(
    formatYMD(new Date(new Date().setDate(today.getDate() - 29))) // last 30 days
  );
  const [endDate, setEndDate] = useState(formatYMD(today));

  const [usageData, setUsageData] = useState(null);
  const [loading, setLoading] = useState(true);     // initial page/customer load
  const [fetching, setFetching] = useState(false);  // data fetch spinner
  const [appliedStart, setAppliedStart] = useState(null); 
  const [appliedEnd, setAppliedEnd] = useState(null); 

  const [invoices, setInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);

  const goToPlanPage = () => navigate('/plans');
  const noRangeChange =
    appliedStart !== null &&
    appliedEnd !== null &&
    appliedStart === startDate &&
    appliedEnd === endDate;
  // --- build month list from customer.created ---
  const generateMonths = (startDateStr) => {
    const monthsArray = [];
    const currentDate = new Date();
    const start = parseCustomerDate(startDateStr);

    const numberOfMonths =
      (currentDate.getFullYear() - start.getFullYear()) * 12 +
      (currentDate.getMonth() - start.getMonth()) + 1;

    for (let i = 0; i < numberOfMonths; i++) {
      const date = new Date(currentDate);
      date.setMonth(currentDate.getMonth() - i);
      monthsArray.push({
        label: date.toLocaleString('default', { month: 'long', year: 'numeric' }),
        value: formatMonthKey(date),
      });
    }
    return monthsArray;
  };

  useEffect(() => {
    if (customer && customer.created) {
      const monthsList = generateMonths(customer.created);
      setMonths(monthsList);
      setMonth(monthsList[0]?.value || formatMonthKey(new Date()));
      setLoading(false);
    }
  }, [customer]);

  // --- fetch helpers ---
  const fetchMonth = useCallback(async (mKey) => {
    return await get(`usage?month=${mKey}`, customer.apiKey);
  }, [customer]);

const fetchRangeDirect = useCallback(async (startY, endY) => {
  const s = toYMDNoPad(startY);
  const e = toYMDNoPad(endY);
  return await get(`usage?start=${s}&end=${e}`, customer.apiKey);
}, [customer]);

  const aggregateForRange = useCallback(async (startY, endY) => {
    const start = dateFromYMD(startY);
    const end = dateFromYMD(endY);
    const keys = monthsBetween(start, end);

    let totalProductCount = 0;
    let productImageCount = 0;

    for (const k of keys) {
      try {
        const m = await fetchMonth(k);
        totalProductCount += Number(m?.totalProductCount || 0);
        productImageCount += Number(m?.productImageCount || 0);
      } catch (e) {
        // ignore a bad month
      }
    }
    return { totalProductCount, productImageCount, aggregated: true };
  }, [fetchMonth]);
  const toYMDNoPad = (s) => {
    const [y, m, d] = s.split('-');
    return `${Number(y)}-${Number(m)}-${Number(d)}`;
  };
  const fetchMonthMode = useCallback(async () => {
    if (!customer?.apiKey) return;
    setFetching(true);
    setUsageData(null);  
    try {
      const resp = await fetchMonth(month);
      setUsageData(resp);
    } catch (e) {
      console.error('Error fetching month usage:', e);
      setUsageData(null);
    } finally {
      setFetching(false);
    }
  }, [customer, month, fetchMonth]);

  const applyRange = useCallback(async () => {
    if (!customer?.apiKey) return;
    setFetching(true);
    setUsageData(null);  
    try {
      // Try direct range endpoint first
      try {
        const resp = await fetchRangeDirect(startDate, endDate);
        if (resp) {
          setUsageData(resp);
          setAppliedStart(startDate); 
          setAppliedEnd(endDate); 
          return;
        }
      } catch (_) {
        // fall back to aggregation
      }
      const agg = await aggregateForRange(startDate, endDate);
      setUsageData(agg);
      setAppliedStart(startDate);
      setAppliedEnd(endDate);
    } catch (e) {
      console.error('Error fetching range usage:', e);
      setUsageData(null);
    } finally {
      setFetching(false);
    }
  }, [customer, startDate, endDate, fetchRangeDirect, aggregateForRange]);

  // Auto-fetch on Month changes (and when switching into month mode)
  useEffect(() => {
    if (!customer?.apiKey) return;
    if (mode === 'month') {
      fetchMonthMode();
    }
  }, [mode, month, customer, fetchMonthMode]);

  // When switching into Range mode, do one initial fetch for current default range
  useEffect(() => {
    if (!customer?.apiKey) return;
    if (mode === 'range') {
      applyRange();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // --- UI helpers ---
  const setPresetDays = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - (days - 1)); // inclusive
    setStartDate(formatYMD(start));
    setEndDate(formatYMD(end));
    // Do NOT auto-fetch here; user must click Apply (keeps UX explicit)
  };

  const datesInvalid = dateFromYMD(startDate) > dateFromYMD(endDate);
  // === Fetch Stripe Invoices ===
    const fetchInvoices = useCallback(async () => {
      if (!customer?.apiKey) return;
      setLoadingInvoices(true);
      try {
        const resp = await post('get-stripe-invoices', customer.apiKey, {'customer_id' : customer.stripeCustomerId});
        if (resp?.data) {
          const parsed = resp.data.map(inv => ({
            id: inv.id,
            number: inv.number,
            amount: (inv.amount_paid / 100).toFixed(2),
            currency: inv.currency.toUpperCase(),
            created: new Date(inv.created * 1000),
            url: inv.hosted_invoice_url,
            status: inv.status
          }));
          setInvoices(parsed);
        }
      } catch (e) {
        console.error('Error fetching invoices:', e);
        setInvoices([]);
      } finally {
        setLoadingInvoices(false);
      }
    }, [customer]);

    useEffect(() => {
      if (customer?.apiKey) fetchInvoices();
    }, [customer, fetchInvoices]);

  return (
    <>
      {loading ? (
        <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <CircularProgress />
        </Container>
      ) : (
        <Container>
          <Typography variant="h4" gutterBottom>Usage</Typography>

          {/* View mode */}
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="subtitle1">View by:</Typography>
            <ToggleButtonGroup
              value={mode}
              exclusive
              onChange={(_, v) => v && setMode(v)}
              size="small"
            >
              <ToggleButton value="month">Month</ToggleButton>
              <ToggleButton value="range">Range</ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          {/* Month picker */}
          {mode === 'month' && (
            <Select
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              displayEmpty
              fullWidth
              variant="outlined"
              sx={{ mb: 2 }}
            >
              {months.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          )}

          {/* Range picker */}
          {mode === 'range' && (
            <Stack spacing={2} sx={{ mb: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography variant="subtitle2">Presets:</Typography>
                <Button size="small" variant="outlined" onClick={() => setPresetDays(30)}>Last 30 days</Button>
                <Button size="small" variant="outlined" onClick={() => setPresetDays(60)}>Last 60 days</Button>
                <Button size="small" variant="outlined" onClick={() => setPresetDays(90)}>Last 90 days</Button>
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  type="date"
                  label="Start"
                  InputLabelProps={{ shrink: true }}
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  fullWidth
                  error={datesInvalid}
                  helperText={datesInvalid ? 'Start date must be on/before end date' : ''}
                />
                <TextField
                  type="date"
                  label="End"
                  InputLabelProps={{ shrink: true }}
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  fullWidth
                  error={datesInvalid}
                />
              </Stack>
              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  onClick={applyRange}
                  disabled={datesInvalid || fetching || noRangeChange}  // CHANGED
                >
                  {fetching ? 'Applying…' : 'Apply'}
                </Button>
              </Stack>
              <Divider />
            </Stack>
          )}

          {/* Usage */}
          {fetching ? (
  <Stack alignItems="center" sx={{ mt: 3 }}>
    <CircularProgress />
  </Stack>
) : usageData ? (
  // NEW: If daily data is present (range), show totals + daily list
  usageData.daily ? (
    <>
      {/* Totals card */}
      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12} md={6}>
          <Paper style={{ padding: 20 }}>
            <Typography variant="h6">Processed Products</Typography>
            <Typography variant="h4">
              {(usageData.totals && usageData.totals.productCount) ?? usageData.totalProductCount ?? 0}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper style={{ padding: 20 }}>
            <Typography variant="h6">Processed Images</Typography>
            <Typography variant="h4">
              {(usageData.totals && usageData.totals.productImageCount) ?? usageData.productImageCount ?? 0}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Daily details (only days with activity) */}
      <Stack spacing={1} sx={{ mt: 3 }}>
        {usageData.daily
          .filter(d =>
            // keep days that have any non-zero activity across known counters
            (d.productCount ?? 0) > 0 ||
            (d.productImageCount ?? 0) > 0 ||
            (d.productCount ?? 0) > 0 ||
            (d.nonProductImageCount ?? 0) > 0 ||
            (d.nonProductPricingCount ?? 0) > 0
          )
          .map(d => (
            <Paper key={d.date} sx={{ p: 2 }}>
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
    {/* This part stays on the left */}
    <Typography variant="subtitle1">
      <strong>{d.date}</strong>
    </Typography>
    
    {/* This part is pushed to the right */}
    <Typography variant="body1">
      Products: {d.productCount ?? 0} Images: {(d.productImageCount ?? 0) + (d.nonProductImageCount ?? 0)}
    </Typography>
  </Box>
</Paper>
          ))}
        {usageData.daily.length === 0 && (
          <Typography variant="body2">No activity in this range.</Typography>
        )}
      </Stack>
    </>
  ) : (
    // Fallback: month view (old behavior)
    <Grid container spacing={3} sx={{ mt: 1 }}>
      <Grid item xs={12} md={6}>
        <Paper style={{ padding: 20 }}>
          <Typography variant="h6">Processed Products</Typography>
          <Typography variant="h4">{usageData.totalProductCount ?? 0}</Typography>
        </Paper>
      </Grid>
      <Grid item xs={12} md={6}>
        <Paper style={{ padding: 20 }}>
          <Typography variant="h6">Processed Images</Typography>
          <Typography variant="h4">{usageData.productImageCount ?? 0}</Typography>
        </Paper>
      </Grid>
    </Grid>
  )
) : (
  <Typography variant="h6" sx={{ mt: 2 }}>
    No usage in the selected {mode === 'month' ? 'month' : 'range'}.
  </Typography>
)}
<>
 <Divider sx={{ mt: 4, mb: 2 }} />
      <Typography variant="h5" gutterBottom>
        Recent Invoices
      </Typography>

      {loadingInvoices ? (
        <Stack alignItems="center" sx={{ mt: 2 }}>
          <CircularProgress />
        </Stack>
      ) : invoices.length === 0 ? (
        <Typography variant="body2" sx={{ mt: 1 }}>No invoices found.</Typography>
      ) : (
        <Stack spacing={2} sx={{ mt: 1 }}>
          {invoices.map((inv) => (
            <Paper key={inv.id} sx={{ p: 2, borderLeft: '5px solid #111111' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Invoice {inv.number}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {inv.created instanceof Date
                        ? inv.created.toLocaleDateString()
                        : new Date(inv.created * 1000).toLocaleDateString()
                      } — {inv.currency} ${inv.amount}
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  href={inv.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ mt: { xs: 1, sm: 0 } }}
                >
                  View Invoice
                </Button>
              </Box>
            </Paper>
          ))}
        </Stack>
      )}
  </>

          {(customer?.hasActiveSubscription == null ||
            customer?.hasActiveSubscription ||
            customer?.overrideSubscription) ? null : (
            <>
              <br />
              You need a plan to refresh your credits
              <br />
              <Button
                variant="contained"
                sx={{
                  backgroundColor: '#111111',
                  color: 'white',
                  textTransform: 'none',
                  ':hover': { backgroundColor: '#000000' },
                  mt: 2,
                }}
                onClick={goToPlanPage}
              >
                See All Plans
              </Button>
            </>
          )}

          {customer?.basePlan && customer?.basePlan?.periodEndDate && !customer?.overrideSubscription && (
            <Typography variant="h6" sx={{ mt: 2 }}>
              Your credits will renew on {formattedDate(customer.basePlan.periodEndDate)}
            </Typography>
          )}
        </Container>
      )}
    </>
  );
};

export default Dashboard;